/**
 * Data layer for `affiliate-referral-builder`. Thin typed fetch wrappers over /api/v1/affiliate-referral-builder.
 * Raw `fetch()` is confined to this file per the dash contract.
 */

export type BonusType = "flat" | "percentage";

export interface AffiliateReferralProgramRow {
	id: string;
	tenant_id: string;
	program_id: string;
	name: string;
	bonus_type: BonusType;
	bonus_amount: number;
	lifetime_months: number;
	is_active: number;
	created_at: number;
	updated_at: number;
}

export interface ListAffiliateReferralBuilderResponse {
	programs: AffiliateReferralProgramRow[];
}

export interface UpsertAffiliateReferralBuilderBody {
	program_id: string;
	name: string;
	bonus_type: BonusType;
	bonus_amount: number;
	lifetime_months: number;
	is_active: boolean;
}

const BASE = "/api/v1/affiliate-referral-builder";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function list(): Promise<ListAffiliateReferralBuilderResponse> {
	const res = await fetch(`${BASE}/programs`, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListAffiliateReferralBuilderResponse>(res);
}

export async function upsertProgram(
	body: UpsertAffiliateReferralBuilderBody,
): Promise<{ program: AffiliateReferralProgramRow }> {
	const res = await fetch(`${BASE}/programs`, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body),
	});
	return parseOrThrow<{ program: AffiliateReferralProgramRow }>(res);
}

export async function deactivateProgram(
	programId: string,
): Promise<{ program: AffiliateReferralProgramRow }> {
	const res = await fetch(`${BASE}/programs/${encodeURIComponent(programId)}`, {
		method: "DELETE",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<{ program: AffiliateReferralProgramRow }>(res);
}

export const affiliateReferralBuilderQueryKeys = {
	all: ["affiliate-referral-builder"] as const,
	list: () => [...affiliateReferralBuilderQueryKeys.all] as const,
	detail: (id: string) => [...affiliateReferralBuilderQueryKeys.all, id] as const,
};
